import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';

import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import moment from "moment";

am4core.useTheme(am4themes_animated);

const toNumberOrNull = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
};

const getGroupLabel = (item, selectedGroup) => {
    const tsMoment = item && item.date_timestamp
        ? moment(item.date_timestamp)
        : item && item.date
            ? moment(item.date)
            : null;

    if (!tsMoment || !tsMoment.isValid()) return 'Unknown';

    if (selectedGroup === 'w') {
        const weekStart = tsMoment.clone().startOf('isoWeek');
        const weekEnd = tsMoment.clone().endOf('isoWeek');
        return `${weekStart.format('DD-MM')} to ${weekEnd.format('DD-MM')}`;
    }

    if (selectedGroup === 'm') {
        const monthStart = tsMoment.clone().startOf('month');
        const monthEnd = tsMoment.clone().endOf('month');
        return `${monthStart.format('DD-MM')} to ${monthEnd.format('DD-MM')}`;
    }

    if (selectedGroup === 'y') {
        const yearStart = tsMoment.clone().startOf('year');
        const yearEnd = tsMoment.clone().endOf('year');
        return `${yearStart.format('MM-YYYY')} to ${yearEnd.format('MM-YYYY')}`;
    }

    return tsMoment.format('DD-MM');
};

const getSpeedMeta = (item) => {
    const overSpeedSummary = item && item.overspeed_summary;
    const speedLimit = toNumberOrNull(
        item && item.speed_limit != null
            ? item.speed_limit
            : item && item.speedLimit != null
                ? item.speedLimit
                : overSpeedSummary && overSpeedSummary.speed_limit
    );

    let avgSpeed = toNumberOrNull(
        item && item.avg_speed != null
            ? item.avg_speed
            : item && item.average_speed != null
                ? item.average_speed
                : item && item.avgSpeed != null
                    ? item.avgSpeed
                    : null
    );

    if (avgSpeed == null && overSpeedSummary && Array.isArray(overSpeedSummary.overspeed_events) && overSpeedSummary.overspeed_events.length) {
        const speedValues = overSpeedSummary.overspeed_events
            .map(evt => toNumberOrNull(evt && evt.average_speed))
            .filter(v => v != null);

        if (speedValues.length) {
            const sum = speedValues.reduce((acc, cur) => acc + cur, 0);
            avgSpeed = sum / speedValues.length;
        }
    }

    return { avgSpeed, speedLimit };
};

const getSpeedByGroup = (rawData = [], selectedGroup = 'd') => {
    const grouped = {};

    (rawData || []).forEach((item) => {
        const label = getGroupLabel(item, selectedGroup);
        if (!grouped[label]) {
            grouped[label] = {
                avgSpeedSum: 0,
                avgSpeedCount: 0,
                speedLimitSum: 0,
                speedLimitCount: 0
            };
        }

        const speedMeta = getSpeedMeta(item);
        if (speedMeta.avgSpeed != null) {
            grouped[label].avgSpeedSum += speedMeta.avgSpeed;
            grouped[label].avgSpeedCount += 1;
        }

        if (speedMeta.speedLimit != null) {
            grouped[label].speedLimitSum += speedMeta.speedLimit;
            grouped[label].speedLimitCount += 1;
        }
    });

    const result = {};
    Object.keys(grouped).forEach((label) => {
        const groupData = grouped[label];
        result[label] = {
            avgSpeed: groupData.avgSpeedCount ? groupData.avgSpeedSum / groupData.avgSpeedCount : null,
            speedLimit: groupData.speedLimitCount ? groupData.speedLimitSum / groupData.speedLimitCount : null
        };
    });
    return result;
};

function MovementChart({ data: docs, rawData = [], assetData, idleFuelRate, standardFuelRate, selectedGroup, loading, ...props }) {
    const [data, setData] = useState([]);
    const [header, setHeader] = useState('--');

    useEffect(() => {
        setData(docs)
    }, [docs]);

    const chart = useRef(null);

    useLayoutEffect(() => {
        if (!loading && data && data.length) {

            let totaldistance_travelled_effective = 0;
            let totaldistance_travelled = 0;
            data.forEach(d => {
                totaldistance_travelled_effective += Number(d.distance_travelled_effective)
                totaldistance_travelled += Number(d.distance_travelled)
            })

            const speedByGroup = getSpeedByGroup(rawData, selectedGroup);
            const chartData = data.map((doc) => ({
                ...doc,
                avg_speed_line: speedByGroup[doc.date] ? speedByGroup[doc.date].avgSpeed : null,
                speed_limit_line: speedByGroup[doc.date] ? speedByGroup[doc.date].speedLimit : null
            }));
            const avgSpeedValues = chartData.map(d => toNumberOrNull(d.avg_speed_line)).filter(v => v != null);
            const speedLimitValues = chartData.map(d => toNumberOrNull(d.speed_limit_line)).filter(v => v != null);
            const hasAvgSpeedSeries = avgSpeedValues.length > 0;
            const hasSpeedLimitSeries = speedLimitValues.length > 0;

            // am4core.addLicense("ch-custom-attribution");
            let x = am4core.create("distanceTravelled", am4charts.XYChart);

            let title = x.titles.create();
            title.text = header;
            title.fontSize = 15;
            title.marginBottom = 18;

            // Export
            x.exporting.menu = new am4core.ExportMenu();
            x.exporting.filePrefix = "daily_movement_" + new Date().getTime();

            x.paddingRight = 20;

            let dateAxis = x.xAxes.push(new am4charts.CategoryAxis());
            dateAxis.dataFields.category = "date";
            dateAxis.title.text = selectedGroup === 'm' ? 'Month' : selectedGroup === 'w' ? 'Week' : selectedGroup === 'y' ? 'Year' : 'Date Range';
            // dateAxis.title.text = "Date"
            // dateAxis.title.rotation = -90;
            // dateAxis.renderer.minGridDistance = 10;
            // dateAxis.title.fill = am4core.color("#ff0000");
            // dateAxis.title.align = "left"; // Align the title to the left
            // dateAxis.title.valign = "top"; // Vertically align the title to the top
            // dateAxis.title.dy = -30; // Adjust the y position
            // dateAxis.renderer.labels.template.rotation = -90;

            let valueAxis = x.yAxes.push(new am4charts.ValueAxis());
            valueAxis.title.text = 'Distance Travelled (KMs)'
            valueAxis.renderer.line.strokeOpacity = 1;
            valueAxis.renderer.line.strokeWidth = 2;
            valueAxis.renderer.line.stroke = am4core.color("#C61AFC");
            valueAxis.renderer.labels.template.fill = am4core.color("#C61AFC");

            let valueAxis2 = x.yAxes.push(new am4charts.ValueAxis());
            valueAxis2.title.text = 'Distance Travelled (Fuel Based)'
            valueAxis2.renderer.opposite = true;
            valueAxis2.renderer.line.strokeOpacity = 1;
            valueAxis2.renderer.line.strokeWidth = 2;
            valueAxis2.renderer.line.stroke = am4core.color("#1AB00B");
            valueAxis2.renderer.labels.template.fill = am4core.color("#1AB00B");

            let speedAxis = null;
            if (hasAvgSpeedSeries || hasSpeedLimitSeries) {
                speedAxis = x.yAxes.push(new am4charts.ValueAxis());
                speedAxis.title.text = 'Speed (Km/h)';
                speedAxis.renderer.opposite = true;
                speedAxis.renderer.line.strokeOpacity = 1;
                speedAxis.renderer.line.strokeWidth = 2;
                speedAxis.renderer.line.stroke = am4core.color("#fa8c16");
                speedAxis.renderer.labels.template.fill = am4core.color("#fa8c16");
                speedAxis.renderer.grid.template.disabled = true;
                speedAxis.extraMin = 0.05;
            }


            let lineSeries = x.series.push(new am4charts.LineSeries());
            lineSeries.name = "Kms (Odometer)";
            lineSeries.dataFields.valueY = "distance_travelled";
            lineSeries.dataFields.categoryX = "date";
            lineSeries.strokeWidth = 2;
            lineSeries.propertyFields.strokeDasharray = "lineDash";
            lineSeries.tooltip.label.textAlign = "middle";
            lineSeries.yAxis = valueAxis;
            lineSeries.stroke = am4core.color("#C61AFC");
            lineSeries.legendSettings.labelText = `Distance. (Total: ${totaldistance_travelled.toFixed(2)} Kms.)`;

            let bullet = lineSeries.bullets.push(new am4charts.Bullet());
            bullet.fill = am4core.color("#fff"); // tooltips grab fill from parent by default
            bullet.tooltipText = "[#C61AFC font-size: 11px]{name} in {dateX}:\n[/][#C61AFC font-size: 15px]{valueY}[/] [#C61AFC]{additional}[/]"
            let circle = bullet.createChild(am4core.Circle);
            circle.radius = 2;
            circle.fill = am4core.color("#fff");
            circle.strokeWidth = 1;

            let lineSeries2 = x.series.push(new am4charts.LineSeries());
            lineSeries2.name = "Kms (Fuel Based)";
            lineSeries2.dataFields.valueY = "distance_travelled_effective";
            lineSeries2.dataFields.categoryX = "date";

            lineSeries2.stroke = am4core.color("#1AB00B");
            lineSeries2.strokeWidth = 1;
            lineSeries2.propertyFields.strokeDasharray = "lineDash";
            lineSeries2.tooltip.label.textAlign = "middle";
            lineSeries2.yAxis = valueAxis2;
            lineSeries2.legendSettings.labelText = `Distance. (Total: ${totaldistance_travelled_effective.toFixed(2)} Kms.)`;


            let bullet2 = lineSeries2.bullets.push(new am4charts.Bullet());
            bullet2.fill = am4core.color("#fff"); // tooltips grab fill from parent by default
            bullet2.tooltipText = "[#1AB00B font-size: 11px]{name} in {dateX}:\n[/][#1AB00B font-size: 15px]{valueY}[/] [#1AB00B]{additional}[/]"

            let circle2 = bullet2.createChild(am4core.Circle);
            circle2.radius = 3;
            circle2.fill = am4core.color("#fff");
            circle2.strokeWidth = 1;

            let avgSpeedSeries = null;
            if (hasAvgSpeedSeries && speedAxis) {
                const avgSpeed = avgSpeedValues.reduce((sum, value) => sum + value, 0) / avgSpeedValues.length;
                avgSpeedSeries = x.series.push(new am4charts.LineSeries());
                avgSpeedSeries.name = "Avg Speed";
                avgSpeedSeries.dataFields.valueY = "avg_speed_line";
                avgSpeedSeries.dataFields.categoryX = "date";
                avgSpeedSeries.yAxis = speedAxis;
                avgSpeedSeries.stroke = am4core.color("#fa8c16");
                avgSpeedSeries.strokeWidth = 2;
                avgSpeedSeries.tooltipText = "[#fa8c16 font-size: 11px]{name} in {categoryX}:\n[/][#fa8c16 font-size: 15px]{valueY} km/h[/]";
                avgSpeedSeries.legendSettings.labelText = `Avg Speed (Mean: ${Number(avgSpeed).toFixed(2)} km/h)`;

                const avgBullet = avgSpeedSeries.bullets.push(new am4charts.Bullet());
                avgBullet.fill = am4core.color("#fff");
                const avgCircle = avgBullet.createChild(am4core.Circle);
                avgCircle.radius = 2;
                avgCircle.fill = am4core.color("#fff");
                avgCircle.strokeWidth = 1;
                avgCircle.stroke = am4core.color("#fa8c16");
            }

            let speedLimitSeries = null;
            if (hasSpeedLimitSeries && speedAxis) {
                const speedLimit = speedLimitValues.reduce((sum, value) => sum + value, 0) / speedLimitValues.length;
                speedLimitSeries = x.series.push(new am4charts.LineSeries());
                speedLimitSeries.name = "Speed Limit";
                speedLimitSeries.dataFields.valueY = "speed_limit_line";
                speedLimitSeries.dataFields.categoryX = "date";
                speedLimitSeries.yAxis = speedAxis;
                speedLimitSeries.stroke = am4core.color("#f5222d");
                speedLimitSeries.strokeWidth = 2;
                speedLimitSeries.strokeDasharray = "4,4";
                speedLimitSeries.tooltipText = "[#f5222d font-size: 11px]{name} in {categoryX}:\n[/][#f5222d font-size: 15px]{valueY} km/h[/]";
                speedLimitSeries.legendSettings.labelText = `Speed Limit (${Number(speedLimit).toFixed(2)} km/h)`;
            }

            x.legend = new am4charts.Legend();

            // Add cursor
            x.cursor = new am4charts.XYCursor();
            x.cursor.fullWidthLineX = true;
            x.cursor.xAxis = dateAxis;
            x.cursor.lineX.strokeOpacity = 0;
            x.cursor.lineX.fill = am4core.color("#000");
            x.cursor.lineX.fillOpacity = 0.1;

            // Add scrollbar
            if (data && data.length > 25) {
                let scrollbarX = new am4charts.XYChartScrollbar();
                scrollbarX.series.push(lineSeries);
                scrollbarX.series.push(lineSeries2);
                if (avgSpeedSeries) scrollbarX.series.push(avgSpeedSeries);
                if (speedLimitSeries) scrollbarX.series.push(speedLimitSeries);
                // scrollbarX.marginBottom = 50;
                x.scrollbarX = scrollbarX;
            }

            x.data = chartData;

            chart.current = x;

            return () => {
                if (x)
                    x.dispose();
            };
        }
    }, [data, header, loading, selectedGroup, rawData]);

    useLayoutEffect(() => {
        if (chart && chart.current)
            chart.current.paddingRight = props.paddingRight;
    }, [props.paddingRight]);

    useLayoutEffect(() => {
        if (selectedGroup && assetData) {
            const title = selectedGroup === 'w' ? 'Weekly' : selectedGroup === 'm' ? 'Monthwise' : selectedGroup === 'y' ? 'Yearwise' : 'Daywise';
            setHeader(`${title} Movement data for ${assetData.assetCode}`)
        }
    }, [selectedGroup, assetData]);

    return <>{loading ? <div style={{ width: "100%", height: "500px", display: 'flex', alignItems: 'center', justifyContent: 'center' }} >Loading ...</div> : <div id="distanceTravelled" style={{ width: "100%", height: "500px" }} />}</>
}

export default MovementChart;
